/**
 * V70 엔글 마블 V2 — 다중 탭 입력 차단 (H1 재구현)
 *
 * V2 변경:
 *   1. Web Locks API 우선 적용 — 브라우저 지원 시 OS 레벨 독점 잠금
 *   2. Fallback: localStorage heartbeat (완전 보장 X — 사용자 지시 §2 "주장 금지")
 *   3. 활성 탭이 살아 있는 동안 **강제 탈취 금지** (BroadcastChannel 양도 요청 → 자발 해제만)
 *   4. 상태 변경 직전 권한 검증용 isActive 제공
 *
 * Web Locks 특성:
 *   - navigator.locks.request(name, options, callback) 로 획득
 *   - callback 안에서 Promise 유지하면 lock 보유 지속
 *   - callback return 시 lock 해제
 *   - unload 시 자동 해제 (브라우저 보장)
 *
 * 한계 (사용자 지시 §2 명시):
 *   - Web Locks 미지원 환경 → heartbeat fallback 은 lost update race window 존재
 *   - 미지원 환경에서는 "안전성 미보장" 상태로 사용자에게 더 보수적인 UI 노출 가능 (hook consumer 가 결정)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const LOCK_KEY_PREFIX = 'marble.lock.';
const TAB_ID_SESSION_KEY = 'marble.tabId';
const HEARTBEAT_MS = 2_000;
const STALE_THRESHOLD_MS = 10_000;
const CHANNEL_NAME = 'marble-tab-sync';
const LOCK_NAME_PREFIX = 'marble-exclusive-';

interface LockRecord {
  tabId: string;
  acquiredAt: number;
  lastHeartbeatMs: number;
}

interface UseTabLockReturn {
  isActive: boolean;
  isLockSupported: boolean;    // Web Locks 지원 여부
  isWebLocksActive: boolean;    // Web Locks 로 획득 (가장 안전) vs heartbeat fallback
  requestControl: () => void;
  release: () => void;
}

function getOrCreateTabId(): string {
  try {
    let id = window.sessionStorage.getItem(TAB_ID_SESSION_KEY);
    if (!id) {
      id = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(TAB_ID_SESSION_KEY, id);
    }
    return id;
  } catch {
    return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function readLockHeartbeat(uid: string): LockRecord | null {
  try {
    const raw = window.localStorage.getItem(LOCK_KEY_PREFIX + uid);
    if (!raw) return null;
    return JSON.parse(raw) as LockRecord;
  } catch { return null; }
}
function writeLockHeartbeat(uid: string, record: LockRecord): void {
  try { window.localStorage.setItem(LOCK_KEY_PREFIX + uid, JSON.stringify(record)); } catch { /* ignore */ }
}
function clearLockHeartbeat(uid: string): void {
  try { window.localStorage.removeItem(LOCK_KEY_PREFIX + uid); } catch { /* ignore */ }
}

function hasWebLocks(): boolean {
  return typeof navigator !== 'undefined' && !!(navigator as unknown as { locks?: unknown }).locks;
}

export function useTabLock(uid: string | null): UseTabLockReturn {
  const tabId = useMemo(() => getOrCreateTabId(), []);
  const [isActive, setIsActive] = useState(false);
  const [isWebLocksActive, setIsWebLocksActive] = useState(false);
  const heartbeatRef = useRef<number | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const releaseWebLockRef = useRef<(() => void) | null>(null);
  // 다른 탭으로부터 "제어권 요청" 수신 시 자발 양도 플래그
  const yieldOnRequestRef = useRef(false);

  // Fail-closed (사용자 지시 §2): Web Locks 미지원 환경은 안전성 보장 불가 → "미지원" 상태로 보고
  // Hook consumer 가 isLockSupported=false 를 감지하여 플레이 자체를 차단해야 함.
  const isLockSupported = typeof window !== 'undefined' && hasWebLocks();

  // ─── Web Locks 획득 ───
  // ifAvailable=true (default): 즉시 시도 — 이미 보유 시 false 반환 (초기 로드용)
  // ifAvailable=false: waiter 등록 — 활성 탭이 release 하면 자동 획득 (ISSUE-03A requestControl 양도 흐름용)
  const acquireWebLock = useCallback((lockUid: string, waiter: boolean = false): Promise<boolean> => {
    const lockName = LOCK_NAME_PREFIX + lockUid;
    const nav = navigator as unknown as {
      locks: {
        request(name: string, opts: { mode: 'exclusive'; ifAvailable?: boolean }, cb: (lock: unknown) => Promise<void> | void): Promise<void>;
      }
    };
    return new Promise<boolean>((resolveOuter) => {
      let acquired = false;
      const opts = waiter
        ? { mode: 'exclusive' as const }                       // waiter: blocking until lock available
        : { mode: 'exclusive' as const, ifAvailable: true };    // 즉시 시도
      nav.locks.request(lockName, opts, (lock) => {
        if (!lock) {
          // 이미 다른 탭 보유 (ifAvailable=true 경로)
          resolveOuter(false);
          return;
        }
        acquired = true;
        resolveOuter(true);
        // callback 내부에서 Promise pending 유지 → lock 보유
        return new Promise<void>((resolveInner) => {
          releaseWebLockRef.current = () => {
            releaseWebLockRef.current = null;
            resolveInner();
          };
        });
      }).catch(() => {
        if (!acquired) resolveOuter(false);
      });
    });
  }, []);

  // 참고: 이전 heartbeat fallback 획득 함수는 fail-closed 적용으로 제거됨 (사용자 지시 §2).
  // heartbeat 저장은 Web Locks 모드에서 다른 탭이 "활성 탭 존재" 를 storage 이벤트로 감지하도록 유지만 함.

  const heartbeat = useCallback((lockUid: string) => {
    const existing = readLockHeartbeat(lockUid);
    if (existing && existing.tabId === tabId) {
      writeLockHeartbeat(lockUid, { ...existing, lastHeartbeatMs: Date.now() });
    }
    // Fail-closed (사용자 지시 §2): heartbeat 로 재획득 하지 않음.
    // Web Locks 가 신뢰 소스이며, 재획득은 Web Locks acquireWebLock 호출로만.
  }, [tabId]);

  const release = useCallback(() => {
    if (!uid) return;
    // Web Lock 자발 해제
    if (releaseWebLockRef.current) {
      try { releaseWebLockRef.current(); } catch { /* ignore */ }
    }
    const existing = readLockHeartbeat(uid);
    if (existing && existing.tabId === tabId) clearLockHeartbeat(uid);
    setIsActive(false);
    setIsWebLocksActive(false);
  }, [uid, tabId]);

  const requestControl = useCallback(() => {
    if (!uid) return;
    // 활성 탭이 살아 있으면 BroadcastChannel 로 양도 요청 전달 (사용자 지시 §2 "강제 탈취 금지")
    try {
      channelRef.current?.postMessage({ type: 'request-control', uid, fromTabId: tabId });
    } catch { /* ignore */ }
    // ISSUE-03A: waiter=true 로 blocking 대기 등록 → 활성 탭이 release 하면 자동 획득.
    //            기존 ifAvailable=true 는 즉시 시도라 활성 탭 release 전에 호출되어 false 로 떨어졌음.
    // Fail-closed: Web Locks 미지원이면 획득 자체 불가 (플레이 차단 유지).
    if (hasWebLocks()) {
      void acquireWebLock(uid, true).then((ok) => {
        if (ok) {
          setIsActive(true);
          setIsWebLocksActive(true);
          // heartbeat 레코드도 갱신 — 다른 탭이 "새 활성 탭 등장" 을 storage 이벤트로 감지
          writeLockHeartbeat(uid, { tabId, acquiredAt: Date.now(), lastHeartbeatMs: Date.now() });
        }
      });
    }
    // Web Locks 미지원 → 획득 안 함. isActive 는 false 유지.
  }, [uid, tabId, acquireWebLock]);

  useEffect(() => {
    if (!uid) {
      setIsActive(false);
      setIsWebLocksActive(false);
      return;
    }
    let cancelled = false;

    // Web Locks 우선 — fail-closed: 미지원 환경은 플레이 차단 (사용자 지시 §2)
    if (hasWebLocks()) {
      void acquireWebLock(uid).then((ok) => {
        if (cancelled) return;
        if (ok) {
          setIsActive(true);
          setIsWebLocksActive(true);
          // Web Locks 모드에서도 heartbeat 는 Shadow 탭 UI 가 "활성 탭 존재" 를 감지하도록 유지
          writeLockHeartbeat(uid, { tabId, acquiredAt: Date.now(), lastHeartbeatMs: Date.now() });
        } else {
          setIsActive(false);
          setIsWebLocksActive(false);
        }
      });
    } else {
      // Web Locks 미지원 → fail-closed. isActive=false 유지.
      // heartbeat 은 미동작 (안전성 보장 안 되는 fallback 사용 금지).
      setIsActive(false);
      setIsWebLocksActive(false);
    }

    // Heartbeat loop (Web Locks 모드도 heartbeat 를 갱신 — Shadow 탭이 "활성 존재" 감지하도록)
    heartbeatRef.current = window.setInterval(() => heartbeat(uid), HEARTBEAT_MS);

    // BroadcastChannel
    try {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current.onmessage = (ev) => {
        if (ev.data?.uid !== uid) return;
        if (ev.data?.type === 'request-control' && ev.data?.fromTabId !== tabId) {
          // 다른 탭이 제어권 요청 — 자발 양도 (사용자 지시 §2 "자발 해제만")
          // 자동 양도. 운영상 모달로 사용자 확인하는 흐름이 더 좋지만 Phase 2 단순화.
          if (isActive) {
            yieldOnRequestRef.current = true;
            release();
          }
        }
      };
    } catch { /* BroadcastChannel 미지원 */ }

    const onStorage = (ev: StorageEvent) => {
      if (ev.key === LOCK_KEY_PREFIX + uid) {
        const next = readLockHeartbeat(uid);
        if (!next) {
          // Lock 해제됨 — Web Locks 지원 시에만 재획득 (fail-closed)
          if (hasWebLocks()) {
            void acquireWebLock(uid).then((ok) => {
              if (ok) {
                setIsActive(true);
                setIsWebLocksActive(true);
              }
            });
          }
        } else if (next.tabId !== tabId) {
          setIsActive(false);
        }
      }
    };
    window.addEventListener('storage', onStorage);

    const onUnload = () => {
      // Web Lock 은 unload 시 자동 해제 (브라우저 보장)
      const existing = readLockHeartbeat(uid);
      if (existing && existing.tabId === tabId) clearLockHeartbeat(uid);
    };
    window.addEventListener('beforeunload', onUnload);

    return () => {
      cancelled = true;
      if (heartbeatRef.current != null) window.clearInterval(heartbeatRef.current);
      try { channelRef.current?.close(); } catch { /* ignore */ }
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('beforeunload', onUnload);
      // Web Lock 해제 + heartbeat 해제
      if (releaseWebLockRef.current) {
        try { releaseWebLockRef.current(); } catch { /* ignore */ }
      }
      const existing = readLockHeartbeat(uid);
      if (existing && existing.tabId === tabId) clearLockHeartbeat(uid);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, tabId]);

  return { isActive, isLockSupported, isWebLocksActive, requestControl, release };
}
